import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Mail, Check, Sun, Moon } from 'lucide-react';
import PasswordInput from '../components/PasswordInput';
import api from '../api/client';

export default function ForgotPassword() {
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try { await api.post('/auth/forgot-password', { email }); setStep(2); }
    catch (err) { setError(err.response?.data?.detail || t('common.error')); }
    setLoading(false);
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try { await api.post('/auth/verify-reset-code', { email, code }); setStep(3); }
    catch (err) { setError(err.response?.data?.detail || t('common.error')); }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try { await api.post('/auth/reset-password', { email, code, new_password: newPassword }); setSuccess(true); }
    catch (err) { setError(err.response?.data?.detail || t('common.error')); }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{background:'var(--bg-primary)'}}>
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6"><Check size={32} className="text-green-400" /></div>
          <h1 className="text-2xl font-black mb-3" style={{color:'var(--text-primary)'}}>{t('auth.passwordReset')}</h1>
          <p className="mb-8" style={{color:'var(--text-muted)'}}>{t('auth.passwordResetSuccess')}</p>
          <Link to="/login" className="btn-primary inline-block" data-testid="back-to-login">{t('auth.login')}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{background:'var(--bg-primary)'}}>
      <button onClick={toggleTheme} className="fixed top-5 right-5 p-3 rounded-full glass-card-hover" style={{color:'var(--text-muted)'}} data-testid="theme-toggle">
        {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
      </button>
      <div className="w-full max-w-md">
        <Link to="/login" className="flex items-center gap-2 mb-8 text-sm" style={{color:'var(--text-muted)'}} data-testid="forgot-back">
          <ArrowLeft size={16} /> {t('auth.backToLogin')}
        </Link>

        <h1 className="text-3xl font-black mb-2" style={{color:'var(--text-primary)'}}>{t('auth.forgotPassword')}</h1>
        <p className="mb-8" style={{color:'var(--text-muted)'}}>
          {step === 1 && t('auth.forgotStep1')}
          {step === 2 && t('auth.forgotStep2')}
          {step === 3 && t('auth.forgotStep3')}
        </p>

        {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-xl px-4 py-3 mb-6" data-testid="forgot-error">{error}</div>}

        {step === 1 && (
          <form onSubmit={handleRequestCode} className="space-y-5">
            <div>
              <label className="block text-sm font-medium mb-2" style={{color:'var(--text-muted)'}}>{t('auth.email')}</label>
              <div className="relative">
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-field !pl-12" placeholder="votre@email.com" required data-testid="forgot-email" />
                <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2" style={{color:'var(--text-faint)'}} />
              </div>
            </div>
            <button type="submit" disabled={loading} className="w-full py-4 rounded-full bg-gradient-to-r from-brand-pink via-rose-500 to-brand-violet text-white font-bold disabled:opacity-50" data-testid="forgot-submit">
              {loading ? t('common.loading') : t('auth.sendCode')}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleVerifyCode} className="space-y-5">
            <div>
              <label className="block text-sm font-medium mb-2" style={{color:'var(--text-muted)'}}>{t('auth.verificationCode')}</label>
              <input type="text" value={code} onChange={(e) => setCode(e.target.value)} className="input-field text-center text-2xl tracking-[0.5em] font-mono" placeholder="000000" maxLength={6} required data-testid="forgot-code" />
            </div>
            <button type="submit" disabled={loading} className="w-full py-4 rounded-full bg-gradient-to-r from-brand-pink via-rose-500 to-brand-violet text-white font-bold disabled:opacity-50" data-testid="forgot-verify">
              {loading ? t('common.loading') : t('auth.verifyCode')}
            </button>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={handleResetPassword} className="space-y-5">
            <div>
              <label className="block text-sm font-medium mb-2" style={{color:'var(--text-muted)'}}>{t('auth.newPassword')}</label>
              <PasswordInput value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min. 6 caracteres" minLength={6} required data-testid="forgot-new-password" />
            </div>
            <button type="submit" disabled={loading} className="w-full py-4 rounded-full bg-gradient-to-r from-brand-pink via-rose-500 to-brand-violet text-white font-bold disabled:opacity-50" data-testid="forgot-reset">
              {loading ? t('common.loading') : t('auth.resetPassword')}
            </button>
          </form>
        )}

        <div className="flex justify-center gap-3 mt-8">
          {[1, 2, 3].map((s) => <div key={s} className={`w-2.5 h-2.5 rounded-full transition-all ${step >= s ? 'bg-brand-violet' : 'bg-white/10'}`} />)}
        </div>
      </div>
    </div>
  );
}
